import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, FileText, Loader2, Calendar, Euro, Save, EyeOff, Eye } from "lucide-react";
import { authHeaders } from "@/hooks/useAdminData";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const currentMonthLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const fmtEuro = (n) =>
  `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtOre = (mins) => {
  const m = Number(mins || 0);
  if (m === 0) return "—";
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h === 0) return `${r}m`;
  if (r === 0) return `${h}h`;
  return `${h}h ${String(r).padStart(2, "0")}m`;
};

export default function AdminTrasferte() {
  const [month, setMonth] = useState(currentMonthLocal);
  const [rows, setRows] = useState([]);
  const [indennitaDefault, setIndennitaDefault] = useState(48);
  const [indennitaInput, setIndennitaInput] = useState("");
  const [savingIndennita, setSavingIndennita] = useState(false);
  const [showExcluded, setShowExcluded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadingZip, setDownloadingZip] = useState(false);

  const load = useCallback(async () => {
    if (!month) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/trasferte?month=${month}`, authHeaders());
      setRows(res.data?.drivers || []);
      setIndennitaDefault(res.data?.indennita_default ?? 48);
      setIndennitaInput(String(res.data?.indennita_default ?? 48));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento trasferte");
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const saveIndennitaDefault = async () => {
    setSavingIndennita(true);
    try {
      await axios.put(`${API}/admin/settings/trasferte`, { indennita: parseFloat(indennitaInput) }, authHeaders());
      toast.success("Indennità giornaliera aggiornata");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore salvataggio indennità");
    } finally {
      setSavingIndennita(false);
    }
  };

  const updateDriver = async (driverId, body, msg) => {
    try {
      await axios.put(`${API}/admin/trasferte/drivers/${driverId}`, body, authHeaders());
      if (msg) toast.success(msg);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiornamento autista");
    }
  };

  const downloadDriverPdf = async (row) => {
    setDownloadingId(row.driver_id);
    try {
      const res = await axios.get(
        `${API}/admin/trasferte/${row.driver_id}/pdf?month=${month}`,
        { ...authHeaders(), responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `prospetto_viaggi_${row.driver_name.replace(/\s+/g, "_")}_${month}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore download PDF");
    } finally {
      setDownloadingId(null);
    }
  };

  const downloadAllZip = async () => {
    setDownloadingZip(true);
    try {
      const res = await axios.get(
        `${API}/admin/trasferte/zip?month=${month}`,
        { ...authHeaders(), responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `prospetti_viaggi_${month}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch (err) {
      const msg = err.response?.data?.detail || "Errore download ZIP";
      // se blob, prova a parsare
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          toast.error(parsed.detail || msg);
        } catch { toast.error(msg); }
      } else {
        toast.error(msg);
      }
    } finally {
      setDownloadingZip(false);
    }
  };

  const enabledRows = useMemo(() => rows.filter((r) => r.trasferte_enabled), [rows]);
  const excludedRows = useMemo(() => rows.filter((r) => !r.trasferte_enabled), [rows]);
  const totals = useMemo(() => ({
    autisti: enabledRows.length,
    giorniLavorati: enabledRows.reduce((s, r) => s + (r.giorni_lavorati || 0), 0),
    giorniFuori: enabledRows.reduce((s, r) => s + (r.giorni_fuori_comune || 0), 0),
    totale: enabledRows.reduce((s, r) => s + (r.totale_deducibile || 0), 0),
  }), [enabledRows]);

  const activeRows = enabledRows.filter((r) => r.giorni_lavorati > 0);
  const hasAnyOutOfComune = totals.giorniFuori > 0;

  return (
    <div className="space-y-6" data-testid="admin-trasferte-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#09090B] font-heading">Prospetto Viaggi</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Riepilogo mensile trasferte autisti (art. 51 c.5 TUIR). Un PDF per autista, {fmtEuro(indennitaDefault)} forfettario per ogni giorno di viaggio fuori dal comune di Castiglione delle Stiviere.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-500">
            <Euro size={14} />
            Indennità/giorno
            <input
              type="number" step="0.01" min="0"
              value={indennitaInput}
              onChange={(e) => setIndennitaInput(e.target.value)}
              className="h-10 w-24 px-3 border border-zinc-200 rounded-sm text-sm font-normal normal-case tracking-normal focus:border-[#FF5200] outline-none"
              data-testid="trasferte-indennita-input"
            />
            <button
              onClick={saveIndennitaDefault}
              disabled={savingIndennita || !(parseFloat(indennitaInput) > 0) || parseFloat(indennitaInput) === indennitaDefault}
              className="inline-flex items-center gap-1 h-10 px-3 border border-zinc-300 text-xs font-bold uppercase tracking-widest hover:border-[#FF5200] hover:text-[#FF5200] disabled:opacity-40 disabled:cursor-not-allowed rounded-sm"
              data-testid="trasferte-indennita-save"
            >
              {savingIndennita ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              Salva
            </button>
          </label>
          <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-zinc-500">
            <Calendar size={14} />
            Mese
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="h-10 px-3 border border-zinc-200 rounded-sm text-sm font-normal normal-case tracking-normal focus:border-[#FF5200] outline-none"
              data-testid="trasferte-month-input"
            />
          </label>
          <button
            onClick={downloadAllZip}
            disabled={downloadingZip || !hasAnyOutOfComune}
            className="inline-flex items-center gap-2 h-10 px-4 bg-[#FF5200] text-white text-xs font-bold uppercase tracking-widest hover:bg-[#e04a00] disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="trasferte-download-zip-btn"
          >
            {downloadingZip ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Scarica tutti (ZIP)
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="border border-zinc-200 rounded-sm p-4 bg-white">
          <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Autisti</p>
          <p className="text-2xl font-bold text-[#09090B] mt-1" data-testid="trasferte-kpi-drivers">{totals.autisti}</p>
        </div>
        <div className="border border-zinc-200 rounded-sm p-4 bg-white">
          <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Giorni lavorati (tot)</p>
          <p className="text-2xl font-bold text-[#09090B] mt-1">{totals.giorniLavorati}</p>
        </div>
        <div className="border border-zinc-200 rounded-sm p-4 bg-white">
          <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Giorni fuori comune</p>
          <p className="text-2xl font-bold text-[#FF5200] mt-1" data-testid="trasferte-kpi-fuori">{totals.giorniFuori}</p>
        </div>
        <div className="border border-zinc-200 rounded-sm p-4 bg-white">
          <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">Totale deducibile</p>
          <p className="text-2xl font-bold text-[#09090B] mt-1" data-testid="trasferte-kpi-total">{fmtEuro(totals.totale)}</p>
        </div>
      </div>

      {/* Tabella autisti */}
      <div className="border border-zinc-200 rounded-sm bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Autista</th>
              <th className="text-right px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Giorni lavorati</th>
              <th className="text-right px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Ore effettive</th>
              <th className="text-right px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Fuori comune</th>
              <th className="text-right px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Indennità/g</th>
              <th className="text-right px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500">Totale deducibile</th>
              <th className="text-right px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-zinc-500">PDF</th>
            </tr>
          </thead>
          <tbody data-testid="trasferte-drivers-tbody">
            {loading && (
              <tr><td colSpan={7} className="text-center py-8 text-zinc-400">
                <Loader2 className="animate-spin inline mr-2" size={14} /> Caricamento...
              </td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-zinc-400">Nessun autista configurato.</td></tr>
            )}
            {!loading && enabledRows.map((r) => (
              <tr key={r.driver_id} className={`border-b border-zinc-100 last:border-0 ${r.giorni_lavorati === 0 ? "opacity-50" : ""}`}
                  data-testid={`trasferte-row-${r.driver_id}`}>
                <td className="px-4 py-3 font-bold text-[#09090B]">{r.driver_name || "-"}</td>
                <td className="px-4 py-3 text-right text-zinc-700">{r.giorni_lavorati}</td>
                <td className="px-4 py-3 text-right text-zinc-700 font-mono text-xs">{fmtOre(r.durata_viaggi_minuti)}</td>
                <td className="px-4 py-3 text-right font-bold text-[#FF5200]">{r.giorni_fuori_comune}</td>
                <td className="px-4 py-3 text-right">
                  <IndennitaCell row={r} fallback={indennitaDefault} onSave={(v) => updateDriver(r.driver_id, { trasferte_indennita: v }, v ? "Indennità autista aggiornata" : "Indennità autista: usa il valore globale")} />
                </td>
                <td className="px-4 py-3 text-right font-bold text-[#09090B]" data-testid={`trasferte-total-${r.driver_id}`}>{fmtEuro(r.totale_deducibile)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    onClick={() => downloadDriverPdf(r)}
                    disabled={downloadingId === r.driver_id || r.giorni_lavorati === 0}
                    className="inline-flex items-center gap-2 px-3 py-1.5 border border-zinc-300 text-xs font-bold uppercase tracking-wider hover:border-[#FF5200] hover:text-[#FF5200] disabled:opacity-40 disabled:cursor-not-allowed rounded-sm"
                    data-testid={`trasferte-download-pdf-${r.driver_id}`}
                  >
                    {downloadingId === r.driver_id ? <Loader2 size={12} className="animate-spin" /> : <FileText size={12} />}
                    Scarica PDF
                  </button>
                  <button
                    onClick={() => updateDriver(r.driver_id, { trasferte_enabled: false }, `${r.driver_name} escluso dal prospetto`)}
                    title="Escludi dal prospetto viaggi"
                    className="ml-2 inline-flex items-center gap-1 px-2 py-1.5 border border-zinc-200 text-zinc-400 text-xs hover:border-red-400 hover:text-red-600 rounded-sm"
                    data-testid={`trasferte-exclude-${r.driver_id}`}
                  >
                    <EyeOff size={12} />
                  </button>
                </td>
              </tr>
            ))}
            {!loading && activeRows.length === 0 && rows.length > 0 && (
              <tr><td colSpan={7} className="text-center py-6 text-zinc-400 italic">
                Nessun autista ha viaggi registrati in {month}.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Autisti esclusi */}
      {excludedRows.length > 0 && (
        <div className="border border-dashed border-zinc-300 rounded-sm bg-white" data-testid="trasferte-excluded-box">
          <button onClick={() => setShowExcluded((v) => !v)} className="w-full flex items-center justify-between px-4 py-3 text-xs font-bold uppercase tracking-widest text-zinc-500 hover:text-[#09090B]" data-testid="trasferte-excluded-toggle">
            <span className="flex items-center gap-2"><EyeOff size={14} /> Autisti esclusi dal prospetto ({excludedRows.length})</span>
            <span>{showExcluded ? "Nascondi" : "Mostra"}</span>
          </button>
          {showExcluded && (
            <ul className="border-t border-zinc-200 divide-y divide-zinc-100">
              {excludedRows.map((r) => (
                <li key={r.driver_id} className="flex items-center justify-between px-4 py-2.5 text-sm" data-testid={`trasferte-excluded-${r.driver_id}`}>
                  <span className="text-zinc-600">{r.driver_name} <span className="text-xs text-zinc-400">· {r.giorni_fuori_comune} gg fuori comune in {month}</span></span>
                  <button onClick={() => updateDriver(r.driver_id, { trasferte_enabled: true }, `${r.driver_name} incluso nel prospetto`)} className="inline-flex items-center gap-1 px-3 py-1.5 border border-zinc-300 text-xs font-bold uppercase tracking-wider hover:border-[#FF5200] hover:text-[#FF5200] rounded-sm" data-testid={`trasferte-include-${r.driver_id}`}>
                    <Eye size={12} /> Includi
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Nota legale */}
      <div className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-sm p-3">
        <span className="font-bold">Nota:</span> il prospetto è compilato secondo l&apos;art. 51 comma 5 del TUIR — l&apos;indennità di trasferta ({fmtEuro(indennitaDefault)}/giorno, modificabile per singolo autista)
        è riconosciuta all&apos;autista quando il viaggio esce dal comune sede di lavoro (Castiglione delle Stiviere).
        Il PDF include una riga per ogni giorno del mese; i giorni non lavorati vengono lasciati vuoti.
      </div>
    </div>
  );
}

function IndennitaCell({ row, fallback, onSave }) {
  const [val, setVal] = useState(row.indennita_override ?? "");
  useEffect(() => { setVal(row.indennita_override ?? ""); }, [row.indennita_override]);
  const commit = () => {
    const n = val === "" ? null : parseFloat(val);
    if ((n ?? null) === (row.indennita_override ?? null)) return;
    onSave(n && n > 0 ? n : null);
  };
  return (
    <div className="inline-flex items-center gap-1">
      <input
        type="number" step="0.01" min="0"
        value={val}
        placeholder={String(fallback)}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        className={`w-20 h-8 px-2 text-right text-sm border rounded-sm outline-none focus:border-[#FF5200] ${row.indennita_override ? "border-[#FF5200] font-bold" : "border-zinc-200 text-zinc-500"}`}
        title="Vuoto = usa l'indennità globale"
        data-testid={`trasferte-driver-indennita-${row.driver_id}`}
      />
      <span className="text-xs text-zinc-400">€</span>
    </div>
  );
}
